import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  loadDocumentTasks,
  loadTaskResponsibles,
  updateTask,
  loadResponsiblesForSingleTask,
  loadTasksCommentsConfig,
  pauseTask,
  startTask
} from 'features/tasks/actionCreators';
import { loadOfferKpi } from 'features/offer/edit/sidebar/actionCreators';
import DocumentTasksListComponent from './DocumentTasksListComponent';
import TaskReasonsComponent from 'features/tasks/components/TaskReasonsComponent';
import ModalTaskResponsibleSelect from 'features/tasks/components/ModalTaskResponsibleSelect';
import { changeValueInArray } from 'utils/functions';
import WrappedFakeText from 'components/common/WrappedFakeText';

let currentTask;

export const DocumentTasksContainer = ({
  userInfo,
  tasksConfig,
  tasksIsChanged,
  tasksLoading,
  taskResponsiblesForSingleTask,
  tasksResponsibles,
  documentTasks,
  actions,
  documentId,
  siteId,
  isFolder
}) => {
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [showResponsibleModal, setShowResponsibleModal] = useState(false);

  const reasonsUpdater = (reasonId, selected) => {
    return {
      ...currentConfig,
      reasons: changeValueInArray(currentConfig.reasons, 'id', reasonId, 'selected', selected)
    };
  };

  useEffect(() => {
    actions.loadTasksCommentsConfig();
    actions.loadTaskResponsibles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tasksIsChanged) {
      actions.loadDocumentTasks(documentId, siteId);
      if (!isFolder) actions.loadOfferKpi(documentId, siteId, tasksIsChanged);
    }
  }, [tasksIsChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  const onOpenModal = task => () => {
    currentTask = task;
    setShowResponsibleModal(true);
    actions.loadResponsiblesForSingleTask(task.taskId, '');
  };

  const onTaskResponsibleSave = task => responsibleId => () => {
    actions.updateTask(task, responsibleId);
    onCloseModal();
  };

  const onSaveMeAsResponsible = task => () => {
    actions.updateTask(task, userInfo.userId);
    onCloseModal();
  };

  const onPauseTask = task => () => {
    currentTask = task;
    const taskConfigPaused = tasksConfig.find(a => a.stateId === 3); // paused state id
    if (taskConfigPaused) {
      setShowCommentsDialog(true);
      setCurrentConfig(taskConfigPaused);
    } else {
      actions.pauseTask(task, '', []);
    }
  };

  const onStartTask = task => () => {
    actions.startTask(task);
  };

  const closeReasonsDialog = () => {
    setShowCommentsDialog(false);
  };

  const onSaveReasons = (task, comment, reasonsIds) => {
    if (task && task.status.id === 2) {
      actions.pauseTask(task, comment, reasonsIds);
      closeReasonsDialog();
    }
  };

  const reasonChange = reasonId => event => {
    setCurrentConfig(reasonsUpdater(reasonId, event.target.checked));
  };

  const onCloseModal = () => {
    currentTask = null;
    setShowResponsibleModal(false);
  };

  const getReasonsTask = task => {
    return currentConfig ? (
      <TaskReasonsComponent
        task={task}
        currentConfig={currentConfig}
        onCloseReasonsDialog={closeReasonsDialog}
        showCommentsDialog={showCommentsDialog}
        onSaveReasons={onSaveReasons}
        reasonChange={reasonChange}
      />
    ) : null;
  };

  const getResponsibleModal = task => {
    return (
      <ModalTaskResponsibleSelect
        task={task}
        onAssignResponsible={onTaskResponsibleSave(task)}
        onCloseModal={onCloseModal}
        onAssignToMeResponsible={onSaveMeAsResponsible}
        taskResponsibles={taskResponsiblesForSingleTask?.length !== 0 ? taskResponsiblesForSingleTask : tasksResponsibles}
      />
    );
  };

  return tasksLoading ? (
    <WrappedFakeText lines={7} onPaper />
  ) : (
    <>
      <DocumentTasksListComponent
        documentTasks={documentTasks}
        onOpenModal={onOpenModal}
        onAssignToMe={onSaveMeAsResponsible}
        onPauseTask={onPauseTask}
        isFolder={isFolder}
        onStartTask={onStartTask}
        userInfo={userInfo}
      />
      {showCommentsDialog ? getReasonsTask(currentTask) : null}
      {showResponsibleModal ? getResponsibleModal(currentTask) : null}
    </>
  );
};

DocumentTasksContainer.propTypes = {
  documentTasks: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  userInfo: PropTypes.object.isRequired,
  tasksConfig: PropTypes.array.isRequired,
  tasksIsChanged: PropTypes.bool.isRequired,
  tasksLoading: PropTypes.bool.isRequired,
  taskResponsiblesForSingleTask: PropTypes.array.isRequired,
  tasksResponsibles: PropTypes.array.isRequired,
  isFolder: PropTypes.bool.isRequired
};

function mapStateToProps(state, route) {
  const { tasksConfig, taskResponsiblesForSingleTask, tasksResponsibles, tasksIsChanged } = state.tasksData;
  const key = `${route.documentId}-${route.siteId}`;

  return {
    userInfo: state.userInfo,
    tasksConfig: tasksConfig,
    tasksIsChanged: tasksIsChanged,
    tasksLoading: state.tasksData[key]?.tasksLoading,
    taskResponsiblesForSingleTask: taskResponsiblesForSingleTask,
    tasksResponsibles: tasksResponsibles
    //loading: state.ajaxCallsInProgress > 0
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadDocumentTasks,
        loadOfferKpi,
        loadTaskResponsibles,
        updateTask,
        loadResponsiblesForSingleTask,
        loadTasksCommentsConfig,
        pauseTask,
        startTask
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(DocumentTasksContainer);
